import store from '../../redux/store';
import { showMessageModal } from '../../redux/actions';
import { sendCloudWatchAlert } from '../../utils/logs';
import { createCustomerBackend } from '../backend';
import { getFirebaseAuth } from './firebaseInit';
import { tryCatch } from 'topkat-utils';
import {
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { isStringNotNull } from '../../utils';
import { transferCustomerData } from '../users';

//----------------------------------------
// FIREBASE AUTHENTICATION SERVICES
//----------------------------------------
export const getFirebaseCustomerId = () => {
  const auth = getFirebaseAuth();
  const { currentUser } = auth || {};
  return currentUser ? currentUser.uid : null;
};

const lastTokenDate = Date.now();
const tokenRefreshTimeMs = 10 * 60 * 1000;

export const getIdToken = async (forceRefresh = false) => {
  const auth = getFirebaseAuth();
  if (lastTokenDate < Date.now() - tokenRefreshTimeMs) forceRefresh = true;
  return await auth?.currentUser?.getIdToken(forceRefresh);
};
// FORCE refresh token at least once per hour, that's the firebase token life
// => https://firebase.google.com/docs/auth/admin/manage-sessions
setInterval(() => {
  tryCatch(() => {
    console.log(`Refreshing token`);
    getIdToken(true);
  });
}, tokenRefreshTimeMs);

export const signOut = async () => {
  const { user: stateUser } = store.getState().userReducer;
  const auth = getFirebaseAuth();
  await auth.signOut();
  const anonymousUser = await anonymousUserConnexion();
  if (
    isStringNotNull(stateUser?.customerId) &&
    isStringNotNull(anonymousUser?.user?.uid) &&
    stateUser?.customerId !== anonymousUser?.user?.uid
  ) {
    await transferCustomerData(stateUser?.customerId, anonymousUser.user.uid);
  }
};

export const signInUser = async (email, password) => {
  const auth = getFirebaseAuth();
  try {
    const response = await signInWithEmailAndPassword(auth, email, password);
    return response.user.uid;
  } catch (error) {
    const errorMessage = String(error);
    if (
      !errorMessage.includes('already') &&
      !errorMessage.includes('6 characters') &&
      !errorMessage.includes('user does not have a password') &&
      !errorMessage.includes('There is no user record')
    ) {
      sendCloudWatchAlert(`Error login ${errorMessage}`);
    }
    const errorCode = error.code;
    throw errorCode;
  }
};

export const deleteUser = async () => {
  // TODO delete user also in backend. But this should be a
  // backend transaction to avoid coherence issues
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (user) {
    try {
      sendCloudWatchAlert(`Delete user ${user.uid}`);
      await user.delete();
    } catch (error) {
      sendCloudWatchAlert(`Could not delete user ${error}`);
      throw error;
    }
  }
};

export const FIREBASE_USER_FLAG = 'pendingUser';

export const generatePendingUserFlag = (customerId) =>
  `${FIREBASE_USER_FLAG}_${customerId}`;

export const signUpUser = async (
  email,
  password,
  lastName,
  firstName,
  numero,
  phone,
  newsletter
) => {
  const auth = getFirebaseAuth();
  try {
    const response = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = response.user;
    const customerId = user.uid;
    await updateProfile(user, {
      displayName: generatePendingUserFlag(customerId),
    });
    const profile = {
      firstName,
      lastName,
      email,
      numero,
      phone,
      newsletter,
      id: customerId,
    };
    await createCustomerBackend(profile);
    return customerId;
  } catch (error) {
    const errorMessage = String(error);
    sendCloudWatchAlert(`Error sign up ${errorMessage}`);
    const errorCode = error.code;
    throw errorCode;
  }
};

export const forgetPasswordUser = async (email) => {
  const auth = getFirebaseAuth();
  try {
    await sendPasswordResetEmail(auth, email);
    store.dispatch(
      showMessageModal(
        'Un email vous a été envoyé, cliquez sur le lien dans celui-ci pour réinitialiser votre mot de passe'
      )
    );
  } catch (error) {
    const errorCode = error.code;
    throw errorCode;
  }
};

export const ckeckUserAccount = async (email) => {
  const auth = getFirebaseAuth();
  try {
    const data = await fetchSignInMethodsForEmail(auth, email);
    return data;
  } catch (error) {
    sendCloudWatchAlert(`Error fetching user with email ${error}`);
    throw error;
  }
};

export const anonymousUserConnexion = async () => {
  const auth = getFirebaseAuth();
  try {
    const response = await signInAnonymously(auth);
    return response.user.uid;
  } catch (error) {
    sendCloudWatchAlert(`Error sign in anonymous user ${error}`);
    throw error;
  }
};
